import { cn } from "@/infrastructure/lib/utils";
import React, { useEffect, useRef } from "react";

interface LoadingSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "md" | "lg";
  color?: string;
}

/**
 * Componente personalizado de spinner de carregamento do Prodgy.
 * Utiliza uma animação de lemniscata (símbolo do infinito) com dois tons de azul
 * para reforçar a identidade visual da marca.
 */
export function LoadingSpinner({ 
  size = "lg",
  className,
  color,
  ...props 
}: LoadingSpinnerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  
  // Mapeando os tamanhos para valores de canvas - aumentados em 2x conforme solicitado
  const sizeValues = {
    sm: { width: 120, height: 120, lineWidth: 4 },
    md: { width: 240, height: 240, lineWidth: 6 },
    lg: { width: 360, height: 360, lineWidth: 8 }
  };
  
  // Não é necessário definir cores aqui, serão calculadas dentro do useEffect

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Recalcular se é branco caso a classe ou a prop mude
    const isWhite = className?.includes("text-white") || color === "white";
    const gradientColor1 = isWhite ? "#FFFFFF" : "#006EC7";
    const gradientColor2 = isWhite ? "#FFFFFF" : "#1974BD";
    const gradientColor3 = isWhite ? "#FFFFFF" : "#438AC5";

    const { width, height, lineWidth } = sizeValues[size];
    canvas.width = width;
    canvas.height = height;

    let counter = 0;
    const centerX = width / 2;
    const centerY = height / 2;
    // Aumentando a escala em 2x para tornar a lemniscata maior
    const scale = size === "lg" ? 60 : (size === "md" ? 40 : 20);

    // Função que desenha a lemniscata (símbolo do infinito)
    const drawLemniscate = () => {
      ctx.clearRect(0, 0, width, height);

      // Parâmetros da lemniscata
      const a = scale;
      const horizontalFactor = 1.0; // Alterado para 1.0 para tornar o percurso mais circular

      // Removido o caminho de referência conforme solicitado

      // Rastro único percorrendo todo o caminho da lemniscata
      ctx.beginPath();
      for (let i = 0; i < 100; i++) {
        const angle = counter - i * 0.025; // Um ângulo menor para cobrir mais do caminho

        // Fórmula modificada para criar loops quase circulares na lemniscata
        // Usando um fator de circularidate mais agressivo para aproximar os loops de círculos perfeitos
        const circularityFactor = 0.65; // Quanto menor, mais circulares são os loops

        // Calculando o denominador com o fator de circularidade para criar loops mais redondos
        const denom = 1 + Math.pow(Math.sin(angle), 2) * circularityFactor;

        // Aplicando a fórmula ajustada para criar loops mais circulares
        const x = centerX + horizontalFactor * (a * Math.cos(angle)) / denom;
        const y = centerY + (a * Math.sin(angle) * Math.cos(angle)) / denom;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.globalAlpha = 1.0; // Restaurando opacidade total

      // Criando um gradiente radial centrado na aresta
      // O gradiente começa do ponto central (início do rastro) e se expande para fora
      const startX = centerX + horizontalFactor * (a * Math.cos(counter)) / (1 + Math.sin(counter) * Math.sin(counter));
      const startY = centerY + (a * Math.sin(counter) * Math.cos(counter)) / (1 + Math.sin(counter) * Math.sin(counter));

      // Criando um gradiente linear ao longo do rastro
      const gradient = ctx.createLinearGradient(
        startX, startY, // Começo do rastro
        startX - 100 * Math.cos(counter), startY - 100 * Math.sin(counter) // Direção do gradiente
      );

      // Definindo as paradas do gradiente com as cores especificadas
      gradient.addColorStop(0, gradientColor1);  // Cor mais interna
      gradient.addColorStop(0.5, gradientColor2); // Cor intermediária
      gradient.addColorStop(1, gradientColor3);   // Cor mais externa

      ctx.strokeStyle = gradient;
      ctx.lineWidth = lineWidth * 2.2; // Espessura aumentada da aresta
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();

      // Atualizar contador para a próxima animação
      counter += 0.09; // Aumentando ainda mais a velocidade da animação
      if (counter >= 2 * Math.PI) {
        counter = 0;
      }
    };

    // Função de animação
    const animate = () => {
      drawLemniscate();
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup ao desmontar
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn("flex items-center justify-center", className)}
      {...props}
    >
      <canvas 
        ref={canvasRef}
        className="rounded-lg"
        style={{ width: sizeValues[size].width, height: sizeValues[size].height }}
      />
      <span className="sr-only">Loading...</span>
    </div>
  );
}
